import axios from 'axios';
import { catchError } from './common';
import qs from 'qs';
import type { SkillPayload } from '@/components/skill-builder/skillBuilder.types';

export const createAiApp = async (payload: any) => {
  const response = await axios.post(
    `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/agents/`,
    payload
  );
  return response;
};

export const getAiApp = async (agentId) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/agents/${agentId}`
    );
    return response?.data?.data;
  } catch (error: any) {
    console.error(error);
    if (error?.status === 404) {
      return { agent_status: 'not-found', code: error?.status };
    }
    return { agent_status: 'read-only' };
  }
};

export const updateAiApp = async (agentId: string, payload: any) => {
  const response = await axios.put(
    `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/agents/${agentId}`,
    payload
  );
  return response;
};

export const deleteAiApp = async (agentId: string) => {
  const response = await axios.delete(
    `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/agents/${agentId}`
  );
  return response;
};

export const uploadAppIcon = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/assets/icons/`,
      formData
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/assets/images/`,
      formData
    );
    return response?.data?.data;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const getAppIcon = async (image_id) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/assets/icons/${image_id}`,
      {
        responseType: 'arraybuffer',
      }
    );
    const blob = new Blob([response.data], {
      type: response.headers['content-type'] || 'image/png',
    });

    return URL.createObjectURL(blob);
  } catch (error) {
    console.error(error);
  }
};

export const getImageFromImageId = async (image_id) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/assets/images/${image_id}`,
      {
        responseType: 'arraybuffer',
      }
    );
    const blob = new Blob([response.data], {
      type: response.headers['content-type'] || 'image/png',
    });

    return URL.createObjectURL(blob);
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const deleteAppIcon = async (image_id) => {
  try {
    const response = await axios.delete(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/assets/icons/${image_id}`
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const deleteImage = async (image_id) => {
  try {
    const response = await axios.delete(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/assets/images/${image_id}`
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const getAppsByCategory = async (type: string, sort_by?: string, sort_order?: string) => {
  try {
    const params: Record<string, string> = { type };
    if (sort_by) {
      params.sort_by = sort_by;
      if (sort_by === 'name' && sort_order) {
        params.sort_order = sort_order;
      }
    }
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/agents`,
      {
        params,
        paramsSerializer: (param: Record<string, any>) => {
          return new URLSearchParams(param).toString().replaceAll('+', '%20');
        },
      }
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const getIconById = async (id, signal?) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/assets/icons/${id}`,
      {
        responseType: 'arraybuffer',
        signal: signal,
      }
    );
    return response;
  } catch (error) {
    console.error(error);
  }
};

export const getUserDetails = async (query: string, limit: number) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/profile/search-user`,
      {
        params: { search: query, limit: limit },
      }
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const getLocations = async () => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/locations`
    );
    return response;
  } catch (err) {
    console.error(err);
    catchError(err);
  }
};

export const getDivisions = async () => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/divisions`
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const getSharedAppDetails = async (agent_id: string) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/agents/${agent_id}/permissions`
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const shareAppDetails = async (payload: object, agent_id: string) => {
  try {
    const response = await axios.put(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/agents/${agent_id}/permissions`,
      payload
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const updateAppStatus = async (agentId, agentStatus) => {
  try {
    const payload = { agent_status: agentStatus };
    const response = await axios.put(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/agents/${agentId}/status`,
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error(error);
    catchError(error?.response?.data);
  }
};

export const validateDocuments = async (
  path: string,
  title: string,
  is_sync_flow = false
): Promise<any> => {
  try {
    const data = { path: path, title: title, is_sync_flow: is_sync_flow };
    const response = await axios.post(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai-ingestion/validate-documents`,
      data
    );
    return response.data;
  } catch (e: any) {
    console.log(e);
    throw e;
  }
};

export const updateMetrics = async (agentId, sessionId, key, enabled?) => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/metrics_data/add_metric`,
      {
        agent_id: agentId,
        session_id: sessionId,
        metric_value: {
          key: key,
          enable: enabled,
        },
      }
    );
    return response;
  } catch (error) {
    console.error(error);
  }
};

export const getKnowledgeSources = async (
  myContentOnlyFlag: string,
  context?: string
): Promise<SourceList> => {
  try {
    const data = { retrieve_only_processed_sources: myContentOnlyFlag };
    if (context) data['context'] = context;
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/content/sources?${qs.stringify(data)}`
    );
    return response.data.data;
  } catch (error: any) {
    console.error(error);
    catchError(error);
    let retObj: any;
    return retObj;
  }
};

export const downloadTranscriptions = async (transcriptionsId: string) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/assets/transcriptions/${transcriptionsId}`
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const deleteTrascription = async (transcriptionsId: string) => {
  try {
    const response = await axios.delete(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/assets/transcriptions/${transcriptionsId}`
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const getProfitCenters = async (userId: string) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/finance-app/profit-centers/users/${userId}`
    );
    return response?.data;
  } catch (error) {
    console.error(error);
  }
};

export const getFinanceAgentLocations = async (userId: string) => {
  try {
    const respone = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/finance-app/locations/users/${userId}`
    );
    return respone?.data;
  } catch (error) {
    console.error(error);
  }
};

export const getFinanceAgentPermissions = async (userId: string) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/finance-app/extract-permissions/users/${userId}`
    );
    return response?.data;
  } catch (error) {
    console.error(error);
  }
};

export const extractFilters = async (reqBody) => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/finance-app/extract-filters/`,
      reqBody
    );
    return response?.data;
  } catch (error) {
    console.error(error);
  }
};

export const getSearchApp = async (query: string) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/agents/search`,
      {
        params: { query: query },
      }
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const getFavouriteAppList = async () => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/agents/favourite`
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const toggleAppFavoriteStatus = async (data) => {
  try {
    const response = await axios.patch(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/agents/favourite`,
      data
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const updateAppRating = async (data) => {
  try {
    const response = await axios.patch(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/agents/rating`,
      data
    );
    return response;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

export const updateAppGovenrnanceAgreement = async (type, agreementId) => {
  try {
    const response = await axios.put(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/profile/self/agreements/${agreementId}`,
      { status: type }
    );
    return response?.data?.data;
  } catch (error) {
    console.error(error);
    catchError(error);
  }
};

const SKILLS_BASE = () =>
  `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/skills`;

export const createSkill = async (payload: SkillPayload) => {
  const response = await axios.post(SKILLS_BASE(), payload);
  return response?.data?.data;
};

export const getSkill = async (skillId: string) => {
  const response = await axios.get(`${SKILLS_BASE()}/${encodeURIComponent(skillId)}`);
  return response?.data?.data;
};

export const updateSkill = async (skillId: string, payload: SkillPayload) => {
  const response = await axios.put(`${SKILLS_BASE()}/${encodeURIComponent(skillId)}`, payload);
  return response?.data?.data;
};

export const deleteSkill = async (skillId: string) => {
  const response = await axios.delete(`${SKILLS_BASE()}/${encodeURIComponent(skillId)}`);
  return response?.data?.data;
};

export const improveSkillInstructions = async (instructions: string): Promise<string> => {
  const response = await axios.post(`${SKILLS_BASE()}/improve`, { instructions });
  return response?.data?.data?.instructions ?? '';
};

export type SkillNameCheck =
  | { outcome: 'available'; skillShortcut: string | null }
  | { outcome: 'taken'; message: string }
  | { outcome: 'failed'; message: string };

export const validateSkillName = async (name: string): Promise<SkillNameCheck> => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_DOMAIN}${import.meta.env.VITE_API_VERSION}/ai/validate-name/skill?name=${encodeURIComponent(name)}`
    );
    const body = response?.data;

    if (body?.status === 'error') {
      return { outcome: 'taken', message: body?.message || 'This skill name is already in use.' };
    }

    return { outcome: 'available', skillShortcut: body?.data?.skill_shortcut ?? null };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      return {
        outcome: 'taken',
        message: error.response?.data?.message || 'This skill name is already in use.',
      };
    }

    console.error('Error validating skill name:', error);
    return { outcome: 'failed', message: 'Could not verify this name. Please try again.' };
  }
};
